import * as React from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Activity, Square } from "lucide-react";
import { PageHeader } from "@/components/page-header";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Spinner } from "@/components/ui/spinner";
import { Badge } from "@/components/ui/badge";
import { DataTable } from "@/components/data-table";
import { EmptyState } from "@/components/ui/empty";
import { Pagination } from "@/components/pagination";
import { useConfirm } from "@/components/ui/confirm-dialog";
import { cancelSession, listSessions } from "@/api/constructor";
import { apiErrorMessage } from "@/lib/api";
import { cn, formatDate, formatRelativeTime } from "@/lib/utils";
import { formatUsd } from "@/lib/money";
import type { ConstructorSessionSummary } from "@/types/api";

const PAGE_SIZE = 50;

type Tab = "all" | "running" | "idle" | "error";

const TABS: { id: Tab; label: string }[] = [
    { id: "all", label: "All" },
    { id: "running", label: "Running" },
    { id: "idle", label: "Idle" },
    { id: "error", label: "Error" },
];

function StatusBadge({ status }: { status: string }): React.ReactNode {
    switch (status) {
        case "running":
            return <Badge variant="default">running</Badge>;
        case "idle":
            return <Badge variant="muted">idle</Badge>;
        case "error":
            return <Badge variant="destructive">error</Badge>;
        case "cancelled":
            return <Badge variant="warning">cancelled</Badge>;
        default:
            return <Badge variant="outline">{status}</Badge>;
    }
}

/**
 * Global AI-session list — the out-of-chat visibility + kill surface.
 * Shows every Constructor agent session with its accrued cost so an
 * operator can spot and Stop a runaway without knowing which draft it
 * belongs to.
 */
export function SessionsPage(): React.ReactNode {
    const queryClient = useQueryClient();
    const confirm = useConfirm();
    const [tab, setTab] = React.useState<Tab>("all");
    const [page, setPage] = React.useState(0);

    const sessionsQuery = useQuery({
        queryKey: ["constructor-sessions", tab, page],
        queryFn: () => listSessions(tab === "all" ? undefined : tab, page, PAGE_SIZE),
        placeholderData: (prev) => prev,
        // Live view while agents work: poll every 5s when the Running filter
        // is active or any visible row is still running; otherwise stay quiet.
        refetchInterval: (query) => {
            const rows = query.state.data?.content ?? [];
            const live = tab === "running" || rows.some((s) => s.status === "running");
            return live ? 5_000 : false;
        },
    });

    const stopMut = useMutation({
        mutationFn: (id: number) => cancelSession(id),
        onSuccess: ({ stopped }) => {
            toast.success(stopped ? "Session stopped" : "Session had already finished");
            queryClient.invalidateQueries({ queryKey: ["constructor-sessions"] });
        },
        onError: (err) => toast.error(apiErrorMessage(err, "Couldn't stop session")),
    });

    const confirmStop = (s: ConstructorSessionSummary): void => {
        confirm.show({
            title: "Stop this AI session?",
            description: `Cancels the in-flight agent run on draft #${s.draftId}. The chat and draft files are kept — send a new message to resume.`,
            destructive: true,
            confirmLabel: "Stop session",
            onConfirm: async () => {
                await stopMut.mutateAsync(s.id);
            },
        });
    };

    const rows = sessionsQuery.data?.content ?? [];
    const runningShown = rows.filter((s) => s.status === "running").length;
    // On the Running tab the page total IS the global running count.
    const runningCount =
        tab === "running" ? (sessionsQuery.data?.totalElements ?? runningShown) : runningShown;
    const shownCostMicros = rows.reduce((sum, s) => sum + (s.totalCostUsdMicros ?? 0), 0);
    const polling = tab === "running" || runningShown > 0;

    return (
        <>
            <PageHeader
                title="AI sessions"
                description="Every Constructor agent session across all drafts, with its token spend. Stop a runaway here without opening its chat."
            />
            <div className="p-6 space-y-4">
                {/* Summary strip: running count + cost across the shown sessions. */}
                <Card>
                    <CardContent className="flex flex-wrap items-center gap-x-10 gap-y-3 p-4">
                        <div>
                            <div className="text-xs uppercase tracking-wider text-muted-foreground">Running</div>
                            <div className="text-2xl font-semibold tabular-nums" data-testid="sessions-running-count">
                                {runningCount}
                            </div>
                        </div>
                        <div>
                            <div className="text-xs uppercase tracking-wider text-muted-foreground">
                                Cost · shown sessions
                            </div>
                            <div className="text-2xl font-semibold tabular-nums" data-testid="sessions-shown-cost">
                                {formatUsd(shownCostMicros)}
                            </div>
                        </div>
                        {polling && (
                            <div className="ml-auto flex items-center gap-1.5 text-xs text-muted-foreground">
                                <Activity className="h-3.5 w-3.5" />
                                live · refreshes every 5s
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Status filter tabs */}
                <div className="inline-flex rounded-md border border-border bg-card p-0.5">
                    {TABS.map((t) => (
                        <button
                            key={t.id}
                            type="button"
                            onClick={() => {
                                setTab(t.id);
                                setPage(0);
                            }}
                            className={cn(
                                "rounded px-3 py-1.5 text-sm transition-colors",
                                tab === t.id
                                    ? "bg-background shadow-sm"
                                    : "text-muted-foreground hover:text-foreground",
                            )}
                            data-testid={`sessions-tab-${t.id}`}
                        >
                            {t.label}
                        </button>
                    ))}
                </div>

                {sessionsQuery.isLoading ? (
                    <div className="flex items-center justify-center py-16">
                        <Spinner size="lg" />
                    </div>
                ) : rows.length > 0 ? (
                    <>
                        <DataTable<ConstructorSessionSummary>
                            dataTestid="sessions-table"
                            columns={[
                                {
                                    key: "draft",
                                    header: "Draft",
                                    cell: (s) => (
                                        <Link
                                            to={`/constructor/draft/${s.draftId}`}
                                            className="font-medium text-primary hover:underline"
                                            title="Open this draft in the Constructor"
                                            data-testid={`session-draft-link-${s.id}`}
                                        >
                                            Draft #{s.draftId}
                                        </Link>
                                    ),
                                },
                                {
                                    key: "owner",
                                    header: "Owner",
                                    cell: (s) => (
                                        <span className="text-muted-foreground">{s.ownerEmail ?? "—"}</span>
                                    ),
                                },
                                {
                                    key: "status",
                                    header: "Status",
                                    cell: (s) => <StatusBadge status={s.status} />,
                                },
                                {
                                    key: "model",
                                    header: "Model",
                                    cell: (s) => (
                                        <span className="font-mono text-xs text-muted-foreground">
                                            {s.llmModel ?? "—"}
                                        </span>
                                    ),
                                },
                                {
                                    key: "cost",
                                    header: "Cost",
                                    cell: (s) => (
                                        <span className="tabular-nums">{formatUsd(s.totalCostUsdMicros)}</span>
                                    ),
                                },
                                {
                                    key: "tokens",
                                    header: "Tokens in / out",
                                    cell: (s) => (
                                        <span className="text-xs text-muted-foreground tabular-nums">
                                            {(s.totalInputTokens ?? 0).toLocaleString()} /{" "}
                                            {(s.totalOutputTokens ?? 0).toLocaleString()}
                                        </span>
                                    ),
                                },
                                {
                                    key: "images",
                                    header: "Images",
                                    cell: (s) => <span className="tabular-nums">{s.imageCount ?? 0}</span>,
                                },
                                {
                                    key: "iterations",
                                    header: "Iterations",
                                    cell: (s) => <span className="tabular-nums">{s.iterationCount ?? 0}</span>,
                                },
                                {
                                    key: "lastActivity",
                                    header: "Last activity",
                                    cell: (s) => (
                                        <span
                                            className="text-muted-foreground"
                                            title={formatDate(s.lastActivityAt)}
                                        >
                                            {formatRelativeTime(s.lastActivityAt)}
                                        </span>
                                    ),
                                },
                                {
                                    key: "actions",
                                    header: "",
                                    className: "text-right",
                                    cell: (s) =>
                                        s.status === "running" ? (
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                className="text-destructive hover:text-destructive"
                                                onClick={() => confirmStop(s)}
                                                title="Stop this agent run"
                                                data-testid={`session-stop-${s.id}`}
                                            >
                                                <Square className="h-3.5 w-3.5" /> Stop
                                            </Button>
                                        ) : null,
                                },
                            ]}
                            rows={rows}
                            rowKey={(s) => s.id}
                        />
                        {sessionsQuery.data && (
                            <Pagination
                                page={sessionsQuery.data}
                                pageSize={PAGE_SIZE}
                                currentPage={page}
                                onPage={setPage}
                            />
                        )}
                    </>
                ) : (
                    <EmptyState
                        icon={<Activity className="h-8 w-8" />}
                        title={tab === "all" ? "No AI sessions yet" : `No ${tab} sessions`}
                        description={
                            tab === "all"
                                ? "Constructor chats show up here with their token spend as soon as one is opened."
                                : "Nothing with this status right now. Switch to All to see every session."
                        }
                    />
                )}
            </div>
            {confirm.dialog}
        </>
    );
}
